import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { NAV } from "../constants/testIds";

const links = [
  { to: "/collection", label: "The Collection", id: NAV.collection },
  { to: "/rituals", label: "Deux Rituels", id: "nav-rituals" },
  { to: "/house", label: "The House", id: NAV.house },
  { to: "/journal", label: "Journal", id: NAV.journal },
  { to: "/contact", label: "Contact", id: NAV.contact },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.1, delay: 0.2, ease: [0.2, 0.7, 0.1, 1] }}
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-700 ${
          scrolled ? "backdrop-blur-md bg-[hsl(var(--bone))]/70" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-[72px] md:h-[84px] flex items-center justify-between">
          <Link to="/" data-testid={NAV.logo} className="font-serif-display text-[26px] md:text-[30px] leading-none tracking-tight text-ink">
            korv<span className="text-plum">.</span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `text-[11px] uppercase tracking-editorial font-sans-tight ${
                    isActive ? "text-ink" : "text-ink/70 hover:text-ink"
                  } transition-colors`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <span className="hidden md:inline-flex items-center gap-3 text-[10px] uppercase tracking-editorial text-ink/60 md:ml-20 lg:ml-32">
              <span className="w-8 h-[1px] bg-ink/40 inline-block" />
              Charlotte. London. Accra.
            </span>
            <button
              onClick={() => setOpen(true)}
              className="md:hidden text-[11px] uppercase tracking-editorial"
              aria-label="Open menu"
              data-testid="nav-menu-open"
            >
              Index
            </button>
          </div>
        </div>
        <div className="hairline-soft" />
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-bone md:hidden flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-[72px]">
              <Link to="/" className="font-serif-display text-[26px] leading-none">
                korv<span className="text-plum">.</span>
              </Link>
              <button data-testid="nav-menu-close" onClick={() => setOpen(false)} className="text-[11px] uppercase tracking-editorial">Close</button>
            </div>
            <div className="hairline-soft" />
            <nav className="flex flex-col p-8 gap-6">
              {[{to:"/",label:"Home",id:NAV.home}, ...links].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.7 }}
                >
                  <Link to={l.to} data-testid={l.id} className="font-serif-display text-4xl">
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
